<script setup lang="ts">
  import { CustomCheckbox, SelectionSection, MemberNameStatus } from '@/shared/ui';
  import { isActiveUser } from '@/shared/lib';
  import type { BranchMember } from '@/entities/Branch';
  import type { Position } from '@/entities/Position';

  interface Props {
    sectionKey: string;
    positions: Position[];
    members: BranchMember[];
    selectedPositions: string[];
    selectedUsers: string[];
    description: string;
    descriptionTestId?: string;
  }

  const props = defineProps<Props>();

  const emit = defineEmits<{
    'update:selectedPositions': [value: string[]];
    'update:selectedUsers': [value: string[]];
    toggle: [
      event: Event,
      selected: string[],
      setSelected: (value: string[]) => void,
      itemId: number | string | undefined,
    ];
  }>();

  const getMemberDisplayName = (member: BranchMember) =>
    (member.user?.name?.trim() || member.user?.username?.trim() || member.user?.email) ?? '';

  const getMemberPositionName = (member: BranchMember) =>
    member.positions?.map((position) => position.name).join(', ') ?? '';
</script>

<template>
  <div>
    <div
      :data-testid="props.descriptionTestId"
      class="text-sm font-semibold text-gray-07 mb-4"
    >
      {{ description }}
    </div>

    <div class="flex gap-8">
      <SelectionSection
        title="Cargos"
        select-all-label="Selecionar todos os cargos"
        :items="props.positions"
        :selected-items="props.selectedPositions"
        :count="props.selectedPositions.length"
        count-label="Total de cargos selecionados:"
        @update:selected-items="emit('update:selectedPositions', $event)"
      >
        <template #columns>
          <th class="text-left font-semibold pb-4">
            Cargos
          </th>
        </template>

        <template #item="{ item }">
          <td class="pb-3 flex gap-3 items-center">
            <CustomCheckbox
              :id="`${props.sectionKey}-position-${item.id}`"
              :value="item.id?.toString() ?? ''"
              :checked="props.selectedPositions.includes(item.id?.toString() ?? '')"
              @change="
                (event: Event) =>
                  emit(
                    'toggle',
                    event,
                    props.selectedPositions,
                    (value) => emit('update:selectedPositions', value),
                    item.id
                  )
              "
            />
            {{ item.name }}
          </td>
        </template>
      </SelectionSection>

      <SelectionSection
        title="Colaborador"
        select-all-label="Selecionar todos os colaboradores"
        :items="props.members"
        :selected-items="props.selectedUsers"
        :count="props.selectedUsers.length"
        count-label="Total de colaboradores selecionados:"
        @update:selected-items="emit('update:selectedUsers', $event)"
      >
        <template #columns>
          <th class="text-left font-semibold pb-4">
            Colaborador
          </th>
          <th class="text-left font-semibold pb-4">
            Cargo
          </th>
        </template>

        <template #item="{ item }">
          <td class="pb-4 flex gap-3 items-center">
            <CustomCheckbox
              :id="`${props.sectionKey}-member-${item.id}`"
              :value="item.id?.toString() ?? ''"
              :checked="props.selectedUsers.includes(item.id?.toString() ?? '')"
              @change="
                (event: Event) =>
                  emit(
                    'toggle',
                    event,
                    props.selectedUsers,
                    (value) => emit('update:selectedUsers', value),
                    item.id
                  )
              "
            />
            <MemberNameStatus
              :name="getMemberDisplayName(item)"
              :active="isActiveUser(item.user)"
            />
          </td>
          <td class="pb-4">
            {{ getMemberPositionName(item) }}
          </td>
        </template>
      </SelectionSection>
    </div>
  </div>
</template>
